import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { datasetCatalogApi } from "./datasetCatalogApi";
import {
  catalogCacheEpoch,
  peekCatalogCache,
  writeCatalogCache,
} from "./datasetCatalogCache";
import {
  DatasetCatalogItem,
  DatasetCatalogQuery,
  DatasetCatalogResponse,
  DatasetDiscoverySourceInput,
} from "./datasetCatalogTypes";
import { resolveComputedInstallTitle } from "../../utils/palettePackageFactoryDraft";

const EMPTY_RESPONSE: DatasetCatalogResponse = {
  items: [],
  facets: {
    origin: { source_node: 0, computed: 0, imported: 0, hub: 0 },
    format: {
      csv: 0,
      geojson: 0,
      json: 0,
      parquet: 0,
      geotiff: 0,
      shp: 0,
      bundle: 0,
      osm: 0,
      gpkg: 0,
      gtfs: 0,
      collection: 0,
      onnx: 0,
      netcdf: 0,
    },
  },
};

/** A query flattened for use as a memo key. Field types mirror
 *  DatasetCatalogQuery: this exists to stabilise identity, not to widen. */
type StableCatalogQuery = {
  dataflowId?: string;
  search?: string;
  format?: DatasetCatalogQuery["format"];
  origin?: DatasetCatalogQuery["origin"];
  sort?: DatasetCatalogQuery["sort"];
  includeHub?: boolean;
  groupOsm?: boolean;
  liveOutputs?: DatasetCatalogQuery["liveOutputs"];
};

/** Stable cache key for a catalog fetch (tab filters are client-side only). */
export function catalogFetchKey(query: StableCatalogQuery): string {
  return JSON.stringify({
    dataflowId: query.dataflowId ?? "",
    includeHub: query.includeHub ?? true,
    groupOsm: query.groupOsm ?? false,
    search: query.search ?? "",
    sort: query.sort ?? "recent",
    origin: query.origin ?? "",
    format: query.format ?? "",
    liveOutputs: query.liveOutputs ?? null,
  });
}

export function toStableCatalogQuery(query: DatasetCatalogQuery = {}): StableCatalogQuery {
  return {
    dataflowId: query.dataflowId || undefined,
    // Trimmed here, the sole chokepoint, so a padded query neither reaches the
    // wire nor mints a distinct ``catalogFetchKey`` - which would miss the cache
    // and refetch a result set the server now returns identically (#231).
    search: query.search?.trim() || undefined,
    format: query.format || undefined,
    origin: query.origin || undefined,
    sort: query.sort || "recent",
    includeHub: query.includeHub ?? true,
    groupOsm: query.groupOsm ?? false,
    liveOutputs: query.liveOutputs,
  };
}

/** Warm the shared catalog cache without subscribing (e.g. before opening the drawer). */
export function prefetchDatasetCatalog(query: DatasetCatalogQuery = {}): void {
  const stableQuery = toStableCatalogQuery(query);
  const fetchKey = catalogFetchKey(stableQuery);
  if (peekCatalogCache(fetchKey)) return;

  // Capture the epoch at fetch start: if a mutation invalidates the cache
  // while this prefetch is in flight, its (possibly pre-mutation) response
  // must not be stored.
  const fetchEpoch = catalogCacheEpoch();
  void datasetCatalogApi.listCatalog(stableQuery).then((next) => {
    writeCatalogCache(fetchKey, next, fetchEpoch);
  }).catch(() => {
    /* ignore — the drawer hook will surface errors on open */
  });
}

export type UseDatasetCatalogOptions = DatasetCatalogQuery & {
  /** When false, skip network fetch until enabled (drawer closed). Cache still hydrates UI. */
  enabled?: boolean;
};

export function useDatasetCatalog(query: UseDatasetCatalogOptions = {}) {
  const { enabled = true, ...queryRest } = query;
  const stableQuery = useMemo<StableCatalogQuery>(
    () => toStableCatalogQuery(queryRest),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      queryRest.dataflowId,
      queryRest.search,
      queryRest.format,
      queryRest.origin,
      queryRest.sort,
      queryRest.includeHub,
      queryRest.groupOsm,
      JSON.stringify(queryRest.liveOutputs),
    ],
  );

  const fetchKey = useMemo(() => catalogFetchKey(stableQuery), [stableQuery]);

  const [response, setResponse] = useState<DatasetCatalogResponse>(
    () => peekCatalogCache(fetchKey) ?? EMPTY_RESPONSE,
  );
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const responseRef = useRef<DatasetCatalogResponse>(EMPTY_RESPONSE);
  const fetchGenRef = useRef(0);
  responseRef.current = response;

  const reload = useCallback(async () => {
    if (!enabled) return;
    const gen = ++fetchGenRef.current;
    const cached = peekCatalogCache(fetchKey);

    if (cached) {
      setResponse(cached);
      setRefreshing(true);
      setLoading(false);
    } else if (responseRef.current.items.length > 0) {
      setRefreshing(true);
      setLoading(false);
    } else {
      setLoading(true);
      setRefreshing(false);
    }
    setError(null);

    // Epoch captured at fetch start: a fetch that straddles a cache
    // invalidation may carry pre-mutation data, so the shared cache write is
    // dropped on mismatch. The hook still renders the response (freshest data
    // it has) — the invalidation's refresh event supersedes it right after.
    const fetchEpoch = catalogCacheEpoch();
    try {
      const next = await datasetCatalogApi.listCatalog(stableQuery);
      if (gen !== fetchGenRef.current) {
        return;
      }
      writeCatalogCache(fetchKey, next, fetchEpoch);
      setResponse(next);
      setError(null);
    } catch (err) {
      if (gen !== fetchGenRef.current) {
        return;
      }
      const message = (err as Error)?.message || "Could not load datasets.";
      setError(message);
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [enabled, fetchKey, stableQuery]);

  // Hydrate from shared cache when the fetch key changes (e.g. project load).
  useEffect(() => {
    const cached = peekCatalogCache(fetchKey);
    if (cached) {
      setResponse(cached);
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchKey]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
  }, [enabled, fetchKey, reload]);

  const initialLoading = enabled && loading && response.items.length === 0;

  const install = useCallback(
    async (dataset: DatasetCatalogItem) => {
      if (!stableQuery.dataflowId) {
        throw new Error("Save the dataflow before adding datasets.");
      }
      const item = await datasetCatalogApi.installToDataflow(
        stableQuery.dataflowId,
        dataset.id,
        dataset,
        resolveComputedInstallTitle(dataset),
      );
      await reload();
      return item;
    },
    [reload, stableQuery.dataflowId],
  );

  const uninstall = useCallback(
    async (dataset: DatasetCatalogItem) => {
      if (!stableQuery.dataflowId) return;
      await datasetCatalogApi.uninstallFromDataflow(stableQuery.dataflowId, dataset.id);
      await reload();
    },
    [reload, stableQuery.dataflowId],
  );

  const importDataset = useCallback(
    async (file: File, opts: { discoverySource?: DatasetDiscoverySourceInput } = {}) => {
      // Register-only: importing adds a standalone account-level catalog item
      // and is not attached to any dataflow, so we no longer pass dataflowId.
      // A node/dataflow link is created only on explicit install.
      const item = await datasetCatalogApi.importDataset(file, opts);
      await reload();
      return item;
    },
    [reload],
  );

  return {
    items: response.items,
    facets: response.facets,
    loading: initialLoading,
    refreshing,
    error,
    reload,
    install,
    uninstall,
    importDataset,
  };
}
